import { isAfter, isBefore, subMinutes, addMinutes } from 'date-fns';

import { GetTripDetailsResponse } from '../../services';
import type { TripDetailPermissions } from '../../services';
import { Role, TripStatus } from '../../services/service-trips-api/types';
import { isDateDefined } from '../trips';

// ---------------------------------------------------------------------------
// Dev-only trip permission overrides (dead-code eliminated in production).
// Uses direct process.env.NODE_ENV check (not IS_DEV_BUILD from web app) because
// this shared lib cannot import from the web app — dependency direction is app→lib.
// webpack DefinePlugin replaces process.env.NODE_ENV; Terser folds the constant
// and eliminates the entire function body in production builds.
// ---------------------------------------------------------------------------

const IS_DEV_BUILD = process.env.NODE_ENV !== 'production';

const getTripDevOverrides = (): Partial<TripDetailPermissions> | undefined => {
  if (!IS_DEV_BUILD) return undefined;
  try {
    const win = window as unknown as { __PERMISSION_OVERRIDES__?: { tripDetail?: Partial<TripDetailPermissions> } };
    return win.__PERMISSION_OVERRIDES__?.tripDetail;
  } catch {
    return undefined;
  }
};

// ---------------------------------------------------------------------------

const REBOOK_CUTOFF_MINUTES = 240;

export const isReopenable = (data: GetTripDetailsResponse): boolean => {
  const hasPermission =
    getTripDevOverrides()?.canSeeRebookTripButton ?? Boolean(data?.permissions?.['canSeeRebookTripButton']);
  return (
    hasPermission &&
    isDateDefined(data.pickUpDateTime) &&
    isBefore(new Date(), subMinutes(new Date(data.pickUpDateTime), REBOOK_CUTOFF_MINUTES))
  );
};

const CANCEL_TRIP_LEAD_TIME_MINUTES = 30;

const isWithinCancellationWindow = (pickUpDateTime: GetTripDetailsResponse['pickUpDateTime']): boolean => {
  // No pickup time to evaluate a window against — defer to the backend permission (ENB-2345/ENB-2339).
  if (!isDateDefined(pickUpDateTime)) {
    return true;
  }

  const cancellationCutoff = addMinutes(new Date(), CANCEL_TRIP_LEAD_TIME_MINUTES);
  return isAfter(new Date(pickUpDateTime), cancellationCutoff);
};

export const isCancellable = (data: GetTripDetailsResponse): boolean => {
  const canSeeCancelTripButton =
    getTripDevOverrides()?.canSeeCancelTripButton ?? Boolean(data?.permissions?.['canSeeCancelTripButton']);

  return canSeeCancelTripButton && isWithinCancellationWindow(data.pickUpDateTime);
};

export const isEditable = (data: GetTripDetailsResponse): boolean => {
  return getTripDevOverrides()?.canSeeEditTripButton ?? Boolean(data?.permissions?.['canSeeEditTripButton']);
};

export const shouldShowMap = (data: GetTripDetailsResponse): boolean => {
  return getTripDevOverrides()?.canSeeShowTripMapButton ?? Boolean(data?.permissions?.['canSeeShowTripMapButton']);
};

export const isGuardianOnly = (roles: readonly string[]): boolean =>
  roles.length === 1 && roles[0] === Role.Guardian;

export const resolveAuthRoles = (
  profileRoles?: readonly string[],
  identityRoles?: readonly string[]
): readonly string[] | undefined => (profileRoles?.length ? profileRoles : identityRoles);

export const shouldShowContactDriver = (
  data: GetTripDetailsResponse,
  roles: readonly string[] | undefined
): boolean => {
  if (!roles?.length || isGuardianOnly(roles)) {
    return false;
  }

  return getTripDevOverrides()?.canSeeContactDriverButton ?? Boolean(data?.permissions?.['canSeeContactDriverButton']);
};

const TERMINAL_TRIP_STATUSES: ReadonlySet<TripStatus> = new Set<TripStatus>([
  'COMPLETED',
  'CANCELLED',
  'LATE_CANCEL',
  'NO_SHOW',
  'NO_SHOW_COMPLETED',
  'DROPPED_OFF',
]);

const isTripTerminal = (status: TripStatus | undefined): boolean =>
  !!status && TERMINAL_TRIP_STATUSES.has(status);

export const shouldShowFeedbackButton = (data: GetTripDetailsResponse): boolean =>
  (getTripDevOverrides()?.canSeeFeedbackButton ?? Boolean(data?.permissions?.['canSeeFeedbackButton'])) &&
  isDateDefined(data.pickUpDateTime) &&
  isAfter(new Date(), new Date(data.pickUpDateTime)) &&
  isTripTerminal(data.tripStatus);
